import { gql, useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { useScreenWidth } from "@msys/ui";
import { Box, Stack } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import {
  Outlet,
  useMatch,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { useUserData } from "../../../auth/useUserData.js";
import {
  BreadcrumbItem,
  Page,
  PageTopbarItem,
} from "../../../commons/layout/Page.js";
import { PageColumn } from "../../../commons/layout/PageColumn.js";
import { PageContainer } from "../../../commons/layout/PageContainer.js";
import { QUOTE_TEMPLATE_ITEM_TYPES } from "../../../constants.js";
import { isTreePreviewItemVisible } from "../../../features/doc-items/helpers.js";
import { useQuoteQuickFilters } from "../../../features/quotes/QuoteQuickFilters.js";
import { allowHaveTemplateChildren } from "../../../features/templates/helpers.js";
import { TemplatesQuoteTemplateAction } from "../../../features/templates/quote/TemplatesQuoteTemplateAction.js";
import { createUnnestedTemplateTreeItem } from "../../../features/templates/quote/trees.js";
import { ThreeD } from "../../../features/threeD/ThreeD.js";
import { ThreeDEditorRef } from "../../../features/threeD/ThreeDEditor.js";
import { useFeature } from "../../../../common/FeatureFlags.js";
import { PermissionName } from "../../../../clients/graphqlTypes.js";
import { VirtualBareTreeStandaloneItem } from "../../../trees-virtual/components/VirtualBareTreeStandaloneItem.js";
import { useEnrichExpandedStoreWithParentPathIds } from "../../../trees-virtual/hooks/useEnrichExpandedStoreWithParentPathIds.js";
import { useExpandedStoreWithLocalStorage } from "../../../trees-virtual/hooks/useExpandedStore.js";
import { VirtualItemTree } from "../../../trees-virtual/VirtualItemTree.js";
import { TreeConnector } from "../../../trees/components/TreeConnector.js";
import { TreeToggleAllExpandedButton } from "../../../trees/components/TreeToggleButton.js";
import {
  TemplateQuoteThreeDEdit_ItemFragment,
  useTemplateQuoteThreeDEditQuery,
} from "./TemplateQuoteEditThreeD.generated.js";

interface Props {
  docId: string;
  pathToDocList: string;
  pathToDoc: string;
  pathToDocVersion: string;
  pathToDocPage: string;
  submenuItems: PageTopbarItem[];
  prefixBreadcrumbs: BreadcrumbItem[];
  returnPath?: string | null;
  pageSubHeader: React.ReactElement | null;
}

export const TemplateQuoteThreeDEdit = ({
  docId,
  pathToDocList,
  pathToDoc,
  pathToDocVersion,
  pathToDocPage,
  submenuItems,
  prefixBreadcrumbs,
  pageSubHeader,
}: Props) => {
  const { t } = useTranslate(["Templates"]);
  const match = useMatch(`${pathToDocPage}/items/:itemId`);
  const selectedItemId = match?.params?.itemId ?? null;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnPath = searchParams.get("r");

  const viewer = useUserData().currentUser!;
  const { isMinDesktop, isMinTablet } = useScreenWidth();

  const [treeContainer, setTreeContainer] =
    React.useState<HTMLDivElement | null>(null);

  const [QuickFiltersTabs, quickFilter] =
    useQuoteQuickFilters<TemplateQuoteThreeDEdit_ItemFragment>(false);

  const expandedStore = useExpandedStoreWithLocalStorage(
    "quote-template",
    docId,
    selectedItemId
  );

  const client = useApolloClient();
  const query = useTemplateQuoteThreeDEditQuery({
    client,
    variables: {
      id: docId,
      expandedItemIds: expandedStore.expandedItemIds,
    },
  });

  const doc = getDataOrNull(query.data?.quoteTemplateLatest)?.quoteTemplate;

  const navigateToItem = React.useCallback(
    (id: string) => navigate(`${pathToDocPage}/items/${id}`, { replace: true }),
    [navigate, pathToDocPage]
  );

  const isClientVisibilityEnabled = useFeature("ClientVisibility");

  const viewerPermissions: PermissionName[] = viewer.organisationPermissions;

  const UnnestedTemplateTreeItem = React.useMemo(
    () =>
      createUnnestedTemplateTreeItem(
        pathToDocPage,
        pathToDocList,
        { viewerPermissions },
        docId,
        navigateToItem,
        expandedStore.setItemExpanded
      ),
    [
      pathToDocPage,
      pathToDocList,
      viewerPermissions,
      docId,
      navigateToItem,
      expandedStore.setItemExpanded,
    ]
  );

  const [filteredTreeItems, rootItem, allDocItems] = React.useMemo(() => {
    if (!doc) return [[], undefined, []];
    const allDocItems = doc.items;
    const rootItem = allDocItems?.find(item => item.isRootItem);
    const treeItems = allDocItems;
    const filteredItems = quickFilter(treeItems);
    return [filteredItems, rootItem, allDocItems];
  }, [doc, quickFilter]);

  useEnrichExpandedStoreWithParentPathIds(
    expandedStore,
    allDocItems,
    selectedItemId,
    !query.loading && !query.error
  );

  React.useEffect(() => {
    if (query.loading || query.error) return;
    if (
      selectedItemId &&
      !filteredTreeItems.some(item => item.id === selectedItemId) &&
      selectedItemId !== rootItem?.id
    ) {
      navigate(pathToDocPage, { replace: true });
    }
  }, [
    selectedItemId,
    filteredTreeItems,
    pathToDocPage,
    rootItem,
    navigate,
    query.loading,
    query.error,
  ]);

  const filterItem = React.useCallback(
    (i: TemplateQuoteThreeDEdit_ItemFragment) =>
      isTreePreviewItemVisible(true, i, allDocItems, doc?.isBinding ?? true),
    [allDocItems, doc?.isBinding]
  );

  const threeDEditorRef = React.useRef<ThreeDEditorRef>(null);

  const isRootSelected = selectedItemId === rootItem?.id;

  const isOrganisationTemplate =
    !!doc && doc.owningSystemOrganisationId === viewer.organisation.id;
  const canEdit = isOrganisationTemplate;

  return (
    <Page
      title={doc?.title ?? ""}
      subtitle={t("Document Templates", { ns: "Templates" })}
      submenuItems={submenuItems}
      breadcrumbs={prefixBreadcrumbs}
      action={
        doc ? (
          <TemplatesQuoteTemplateAction
            doc={doc}
            pathToDoc={pathToDoc}
            pathToDocList={pathToDocList}
          />
        ) : undefined
      }
      subHeader={pageSubHeader}
      headerType="template"
    >
      {doc && rootItem && (
        <>
          {isMinDesktop && canEdit && (
            <PageColumn
              $hasRightBorder
              $hasBackground
              $display="block"
              ref={isMinTablet ? setTreeContainer : undefined}
            >
              <Stack direction={"column"} spacing={2}>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    width: "100%",
                    alignItems: "center",
                  }}
                >
                  <TreeToggleAllExpandedButton
                    areAllItemsExpanded={expandedStore.areAllItemsExpanded}
                    setAllItemsExpanded={expandedStore.setAllItemsExpanded}
                  />
                  <QuickFiltersTabs />
                </Stack>

                <Box>
                  <VirtualBareTreeStandaloneItem<TemplateQuoteThreeDEdit_ItemFragment>
                    item={rootItem}
                    items={filteredTreeItems}
                    allItems={allDocItems}
                    depth={0}
                    itemComponent={UnnestedTemplateTreeItem}
                    selected={isRootSelected}
                    sticky={isRootSelected}
                    top={0}
                    clickable
                    onClick={() => navigateToItem(rootItem.id)}
                  />

                  {filteredTreeItems.length > 0 && (
                    <VirtualItemTree<TemplateQuoteThreeDEdit_ItemFragment>
                      items={filteredTreeItems}
                      allItems={allDocItems}
                      filterItem={
                        isClientVisibilityEnabled ? filterItem : undefined
                      }
                      selectedItemId={selectedItemId}
                      enableCreating={false}
                      shouldRenderCreateInput={shouldRenderCreateInput}
                      allowHaveChildren={allowHaveTemplateChildren}
                      documentItemTypes={QUOTE_TEMPLATE_ITEM_TYPES}
                      container={treeContainer}
                      itemComponent={UnnestedTemplateTreeItem}
                      expandedStore={expandedStore}
                    />
                  )}
                </Box>
              </Stack>
            </PageColumn>
          )}

          <Outlet
            context={{
              expandedItemIds: expandedStore.expandedItemIds,
              viewerDecisionRole: undefined,
            }}
          />

          <PageContainer $fullHeight $noPadding>
            <ThreeD
              threeDEditorRef={threeDEditorRef}
              docId={docId}
              projectId={null}
              returnPath={returnPath ?? pathToDocVersion}
              allDocItems={allDocItems}
              isEditable={isMinDesktop && canEdit}
            />
          </PageContainer>
        </>
      )}
    </Page>
  );
};

function shouldRenderCreateInput() {
  return false;
}
